import type { MouseEvent, ReactNode } from "react";
import { Flex, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { ArrowsClockwiseIcon, TrashIcon } from "@phosphor-icons/react";
import {
  Button,
  Card,
  IconButton,
  Input,
  StandoutNotationSwatch,
  type StandoutNotation,
} from "@libertify/ui";
import { KEY, NOTATION_PILLS } from "./constants";
import { PillSegmented } from "../PillSegmented";

export type StandoutEditItemProps = {
  /** Zero-based position, used in the fields' accessible names. */
  index: number;
  title: string;
  notation: StandoutNotation;
  /** The standout's marker color, filling its icon. */
  color: string;
  /** Its mark is the one on the document. */
  active: boolean;
  /** Opens its style controls; clicks on it don't show the passage. */
  styleControl: ReactNode;
  /** Show its passage in the document. */
  onShow: () => void;
  onTitleChange: (title: string) => void;
  onTitleFocus: () => void;
  onNotationChange: (notation: StandoutNotation) => void;
  /** Pick a new passage for it in the document. */
  onReselect: () => void;
  onRemove: () => void;
};

/** Clicks on a row's own controls edit it rather than show its passage. */
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

/**
 * One editable standout: its marker, title, style, delete, marker type and passage. Clicking
 * the row shows its passage; the marker button does the same from the keyboard.
 */
export const StandoutEditItem = ({
  index,
  title,
  notation,
  color,
  active,
  styleControl,
  onShow,
  onTitleChange,
  onTitleFocus,
  onNotationChange,
  onReselect,
  onRemove,
}: StandoutEditItemProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const number = index + 1;

  return (
    <Card
      padding="medium"
      bordered
      shadow="none"
      style={{
        background: token.colorFillQuaternary,
        borderColor: active ? color : undefined,
      }}
      onClick={onShow}
    >
      <Flex vertical gap={10}>
        <Flex align="center" gap={8}>
          <IconButton
            variant="tertiary"
            size="m"
            iconSize={13}
            icon={
              <StandoutNotationSwatch
                notation={notation}
                color={color}
                size={24}
              />
            }
            aria-label={t(`${KEY}.showLabel`, { number })}
            aria-pressed={active}
            onClick={(event) => {
              stopRowClick(event);
              onShow();
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Input
              value={title}
              placeholder={t(`${KEY}.titlePlaceholder`)}
              aria-label={t(`${KEY}.titleLabel`, { number })}
              onClick={stopRowClick}
              onChange={(event) => onTitleChange(event.target.value)}
              onFocus={onTitleFocus}
            />
          </div>
          {styleControl}
          <IconButton
            variant="tertiary"
            size="s"
            iconSize={14}
            icon={<TrashIcon size={14} />}
            aria-label={t(`${KEY}.removeLabel`, { number })}
            onClick={(event) => {
              stopRowClick(event);
              onRemove();
            }}
          />
        </Flex>
        <PillSegmented
          $token={token}
          $wrap
          aria-label={t(`${KEY}.markerLabel`, { number })}
          onClick={stopRowClick}
          value={notation}
          onChange={(value) => {
            const next = NOTATION_PILLS.find((pill) => pill === value);
            if (next) onNotationChange(next);
          }}
          options={NOTATION_PILLS.map((value) => ({
            value,
            label: t(`${KEY}.notation.${value}`),
          }))}
        />
        <Button
          variant="tertiary"
          size="s"
          leftIcon={<ArrowsClockwiseIcon size={13} weight="bold" />}
          style={{ alignSelf: "flex-start" }}
          onClick={(event) => {
            stopRowClick(event);
            onReselect();
          }}
        >
          {t(`${KEY}.reselect.button`)}
        </Button>
      </Flex>
    </Card>
  );
};

export default StandoutEditItem;
